import { useCallback, type ReactNode } from "react";
import { useLocalStorage } from "@/state/state-storage";
import { TradingSettingsContext } from "./trading-settings-context";
import { DEFAULT_LEVERAGE } from "@/solana/constants";
import { useMarginMode } from "@/backend/store";
import type { AccountMode, TradingSettings } from "./types";

type TradingSettingsProviderProps = {
  children: ReactNode;
};

// NOTE: accountMode is NOT stored in localStorage - it comes from backend via Zustand store
// This ensures the backend is the single source of truth for margin mode
const DEFAULT_SETTINGS: TradingSettings = {
  marketLeverages: {},
  slippageTolerance: null, // Auto mode by default (was: 0.01)
  globalDefaultLeverage: DEFAULT_LEVERAGE, // Starts at 1x, user can change in settings
};

export const TradingSettingsProvider = ({
  children,
}: TradingSettingsProviderProps) => {
  const [settings, setSettings] = useLocalStorage<TradingSettings>(
    "solana_perp_trading_settings_v2",
    DEFAULT_SETTINGS
  );

  // Read margin mode from Zustand store (synced from backend)
  // This is the single source of truth - no localStorage duplication
  const storeMarginMode = useMarginMode();
  // Use "isolated" as sensible default when store hasn't loaded yet
  // Isolated is safer for new users (risk per position) vs cross (risk entire account)
  const accountMode: AccountMode = storeMarginMode ?? "isolated";

  // setAccountMode is now a no-op for localStorage - the actual update happens via:
  // 1. API call to backend (in AccountModeDialog)
  // 2. Store sync from backend response
  // This function is kept for API compatibility but doesn't update localStorage
  const setAccountMode = useCallback(
    (_mode: AccountMode) => {
      // No-op: margin mode is updated via backend API, not localStorage
      // The store will be updated when the API response is synced
    },
    []
  );

  // Get leverage for a specific market
  // Priority: per-market override > globalDefaultLeverage > DEFAULT_LEVERAGE fallback
  const getLeverage = useCallback(
    (market: string): number => {
      return settings.marketLeverages[market] ?? settings.globalDefaultLeverage ?? DEFAULT_LEVERAGE;
    },
    [settings.marketLeverages, settings.globalDefaultLeverage]
  );

  // Set leverage for a specific market
  const setLeverage = useCallback(
    (market: string, leverage: number) => {
      setSettings((prev) => ({
        ...prev,
        marketLeverages: {
          ...prev.marketLeverages,
          [market]: leverage,
        },
      }));
    },
    [setSettings]
  );

  // Set slippage tolerance
  const setSlippageTolerance = useCallback(
    (tolerance: number | null) => {
      setSettings((prev) => ({ ...prev, slippageTolerance: tolerance }));
    },
    [setSettings]
  );

  // Global default leverage - applies to new markets without a custom per-market setting
  const defaultLeverage = settings.globalDefaultLeverage ?? DEFAULT_LEVERAGE;

  const setDefaultLeverage = useCallback(
    (leverage: number) => {
      setSettings((prev) => ({ ...prev, globalDefaultLeverage: leverage }));
    },
    [setSettings]
  );

  return (
    <TradingSettingsContext.Provider
      value={{
        accountMode, // From Zustand store (backend), not localStorage
        marketLeverages: settings.marketLeverages,
        slippageTolerance: settings.slippageTolerance ?? null, // Default to Auto
        defaultLeverage,
        getLeverage,
        setLeverage,
        setAccountMode,
        setSlippageTolerance,
        setDefaultLeverage,
      }}
    >
      {children}
    </TradingSettingsContext.Provider>
  );
};
